import type { Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { boolean, object, safeParse, string } from "valibot";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { optionalTextValue, requireText } from "../../utils/validation/base";
import { MAX_CHARACTERS_LG, MAX_CHARACTERS_SM } from "../../constants";
import TextAreaInput from "../../components/inputs/TextAreaInput";
import { isEmpty } from "lodash-es";
import PropertySection from "../PropertySection";
import { HelpButtonPreview } from "../FieldPreviewComponents";
import { keyFromSelector } from "i18next";

const DtoSchema = object({
  title: string(),
  explanation: string(),
  use_help_text: boolean(),
  use_help_text_value: string(),
});

const DomainSchema = object({
  title: requireText(MAX_CHARACTERS_SM),
  explanation: requireText(MAX_CHARACTERS_LG),
  use_help_text_value: optionalTextValue(MAX_CHARACTERS_SM),
});

export type HelpFieldProperties = InferOutput<typeof DomainSchema>;
type HelpFieldDtoType = InferOutput<typeof DtoSchema>;

const HelpField: Field<HelpFieldProperties, HelpFieldDtoType> = {
  type: "help",
  category: "layout",
  widget: "com.moreapps.plugins:help:3",
  schema: DomainSchema,
  hasData: false,
  label: "no_label",
  displayLabel: (properties) => properties.title,
  defaultProperties: () => {
    const { t } = useTranslation();
    return {
      title: t(($) => $.FIELD_PROPERTY_TITLE_TEXT),
      explanation: t(($) => $.FIELD_PROPERTY_EXPLANATION_TEXT),
      use_help_text_value: "",
    };
  },
  icon: "questionMarkCircle",
  titleKey: keyFromSelector(($) => $.HELP_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.HELP_FIELD_DESCRIPTION),
  propertyKeys: {
    title: keyFromSelector(($) => $.FIELD_PROPERTY_TITLE),
    explanation: keyFromSelector(($) => $.FIELD_PROPERTY_EXPLANATION),
    use_help_text_value: keyFromSelector(($) => $.FIELD_PROPERTY_BUTTON_TEXT),
  },
  toDto: (domain) => {
    const { output } = safeParse(DtoSchema, {
      ...domain,
      use_help_text: !isEmpty(domain.use_help_text_value), // To be removed with DEV-4771
    });
    return output as HelpFieldDtoType;
  },
  PreviewContent: (props) => <HelpButtonPreview text={props.properties.use_help_text_value} />,
  EditItem: (props) => {
    const { t } = useTranslation();

    return (
      <PropertySection>
        <TextInput
          label={t(props.propertyKeys.title)}
          value={props.data.properties.title}
          required
          onBlur={(e) => props.setValue("title", e.target.value)}
          errorMessage={props.getError("title")}
        />
        <TextAreaInput
          label={t(($) => $.FIELD_PROPERTY_EXPLANATION)}
          value={props.data.properties.explanation}
          maxLength={MAX_CHARACTERS_LG}
          required
          onBlur={(e) => props.setValue("explanation", e.target.value)}
          errorMessage={props.getError("explanation")}
        />
        <TextInput
          label={t(($) => $.FIELD_PROPERTY_BUTTON_TEXT)}
          value={props.data.properties.use_help_text_value!}
          onBlur={(e) => props.setValue("use_help_text_value", e.target.value)}
          errorMessage={props.getError("use_help_text_value")}
        />
      </PropertySection>
    );
  },
};

export default initField(HelpField);
